// vue-page-title v1.1.6
// Vue.js html/page title manager
// https://github.com/vinicius73/vue-page-title
// (c) 2018-2019 Vinicius Reis <luiz.vinicius73@gmail.com>

/**
 * test if object is a function
 * @method isFunction
 * @param  {any}      object
 * @return {Boolean}
 */
const isFunction = object => typeof (object) === 'function';

/**
 * prevent output undefined
 * @method safeString
 * @param  {String}   st
 * @return {String}
 */
const safeString = st => st || '';

/**
 * build a full title white suffix and prefix
 * @method buildPageTitle
 * @param  {String}       value
 * @param  {Object}       [options={ suffix, prefix }]
 * @return {String}       full title
 */
const buildPageTitle = (value, options) => {
  const { prefix, suffix } = options;

  return `${safeString(prefix)} ${value} ${safeString(suffix)}`.trim()
};

const pageTitleMixin = {
  created () {
    const { title } = this.$options;

    if (title !== undefined) {
      // allow use dinamic title system
      this.$title = isFunction(title)
        ? title.call(this, this)
        : title;
    }
  }
};

/**
 * if use ssr document is not available
 * @method isBrowser
 * @return {Boolean}
 */
const isBrowser = () => (typeof document !== 'undefined');

/**
 * update document.title
 * @method setPageTitle
 * @param  {String}     value new title
 * @param  {Object}     options buildPageTitle options
 * @return {void}
 */
const setPageTitle = (value, options) => {
  // test if not is a browser
  /* istanbul ignore next: SSR */
  if (!isBrowser()) {
    console.warn('no browser enviroment');
    return
  }

  // test if title is empty
  if (safeString(value).length > 0) {
    const { setTitleMethod } = options;
    const title = buildPageTitle(value, options);

    // use custom setTitle method
    if (setTitleMethod && isFunction(setTitleMethod)) {
      setTitleMethod(title);
      return
    }

    document.title = title;
  }
};

/**
 * @method setup
 * @param  {Function}   setTitle    setTitle callback
 * @param  {Object}     options     { router: RouterInstance }
 * @return {void}
 */
const setup = (setTitle, { router }) => {
  router.afterEach((to, from) => {
    const { meta } = to;

    // if has meta and title
    if (meta && meta.title) {
      setTitle(meta.title);
    }
  });
};

const install = (Vue, options = {}) => {
  // prevent double install
  /* istanbul ignore next */
  if (install.installed) return
  install.installed = true;

  // title state
  const $page = {
    title: ''
  };

  const setTitle = value => {
    setPageTitle(value, options);
    $page.title = value;
  };

  // make reactive title
  Vue.util.defineReactive($page, 'title', '');

  // add title to component context
  Object.defineProperty(Vue.prototype, '$title', {
    get: () => $page.title,
    set: value => setTitle(value)
  });

  // vue router support
  if (options.router) {
    setup(setTitle, options);
  }

  // add global mixin
  Vue.mixin(pageTitleMixin);
};

const VuePageTitle = { install };

export default VuePageTitle;
export { install };
